import { ReactElement, useCallback, useState } from "react"

import {
  CellProps,
  NodeValues,
  useSelected,
  useTree,
} from "components/TreeState"
import ConfirmationModal, {
  ModalInfo,
} from "components/Modals/ConfirmationModal"

import Button from "ui/buttons/Button"
import { ChangedPlanRecord } from "../types"
import { Tooltip } from "@material-ui/core"
import { css } from "@emotion/css"
import { get } from "lodash"
import { set } from "lodash/fp"
import { useFeedback } from "components/FeedbackSnackbar"
import { useI18n } from "providers/I18n"
import { useSnapshot } from "valtio"
import { useTableSource } from "../TableStateProvider"
import { useUndoStack } from "../useUndoStack"
import { useLockedRow } from "../useLockedRow"

type Props = CellProps & {
  children: ReactElement
  otherDisabled?: boolean
  canSiblingBeOverwritten?: (row: NodeValues<ChangedPlanRecord>) => Boolean
  applyDependencies?: (
    row: NodeValues<ChangedPlanRecord>,
    value?: any | null,
  ) => NodeValues<ChangedPlanRecord>
}

export function Bulky(props: Props) {
  const {
    rowId: id,
    dataKey,
    children,
    disabled,
    otherDisabled,
    applyDependencies,
    canSiblingBeOverwritten = (_) => true,
    onSave,
  } = props
  const { t } = useI18n()

  const { value: lockedRow } = useLockedRow()

  const [modalInfo, setModalInfo] = useState<ModalInfo>()
  const tableSource = useTableSource()
  const { selections } = useSelected(tableSource)
  const sendFeedback = useFeedback((s) => s.send)
  const readFrom = useSnapshot(tableSource.state)
  const tree = useTree(tableSource)
  const globalUndoState = useUndoStack<ChangedPlanRecord>()
  const writeTo = tableSource.state
  const type = readFrom[id]?.type
  const value = get(readFrom, `${id}.${dataKey}`, null) as string | null

  const node = tree[id]
  const parent = !!node?.parentId ? tree[node.parentId] : undefined
  const selectedChildren = parent?.children?.filter((child) => {
    const isSelected = selections[child]
    const isLineItem = readFrom[child]?.type === "line_item"
    return isSelected && isLineItem
  })

  //
  const onApplyToSelectedSiblings = useCallback(async () => {
    let undoItems: ChangedPlanRecord[] = []
    let warning = false
    let resolved: ChangedPlanRecord[] = []

    if (selectedChildren) {
      selectedChildren.forEach((child) => {
        let updated = { ...writeTo[child] }
        undoItems.push(updated as ChangedPlanRecord)
        const action = readFrom[child]?.action
        const updatable = !action || action === "UPDATE_ORDER"
        if (!!value && canSiblingBeOverwritten(updated)) {
          updated = set(dataKey, value, updated)
        } else {
          warning = true
        }

        // apply dependencies if any
        !!applyDependencies && (updated = applyDependencies(updated, value))

        updatable && (updated = set("action", "UPDATE", updated))

        resolved.push(updated as ChangedPlanRecord)
      })
    }

    // update the state with all re-computed rows by replacing the parent's children
    if (!!resolved.length) {
      const parentId = parent?.id
      if (!!parentId) {
        const oldKey = tree[parentId]?.id
        const parentRow = readFrom[parentId]
        const ancestorId = parentRow.parentId
        const position = ancestorId
          ? tree[ancestorId]?.children?.indexOf(parentId)
          : undefined

        const resolvedIds = resolved.map((r) => r.id)
        const unaffectedIds = parent?.children?.filter(
          (cId) => !resolvedIds.includes(cId),
        )
        const unaffected =
          unaffectedIds?.map((cId) => {
            const child = readFrom[cId] as ChangedPlanRecord
            return { ...child }
          }) || []

        const updatedChildren = [...unaffected, ...resolved]

        tableSource.replaceWithTree(
          oldKey,
          parentRow,
          updatedChildren,
          ancestorId,
          position,
          true,
        )

        const savePlan = !!onSave && (await onSave({ syncUpdate: true }))

        if (!savePlan) {
          globalUndoState.push(undoItems)
        }
      }
    }

    if (warning) sendFeedback(t("bulk.some.failed"), "warning")
    globalUndoState.push(undoItems)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, dataKey, writeTo, readFrom, selectedChildren])

  const isDisabled = disabled || otherDisabled
  const hasSelected = !!selectedChildren?.length
  const canBulkEdit = type === "line_item" && !isDisabled && hasSelected
  const isConfirmationModalOpen = Boolean(modalInfo)

  return canBulkEdit ? (
    <>
      <Tooltip
        classes={{
          tooltip: css`
            background-color: transparent;
            margin: 0;
          `,
        }}
        placement="right"
        title={
          !!value ? (
            <Button
              onClick={() =>
                setModalInfo({
                  type: "primary",
                  submitLabel: t("plans.list.bulk.proceed.action"),
                  title: t("unsaved.plan.title"),
                  description: t("unsaved.plan.bulk.description"),
                  onSubmit: () => {
                    setModalInfo(undefined)
                    onApplyToSelectedSiblings()
                  },
                })
              }
              variant="contained"
              color="primary"
            >
              Apply to all
            </Button>
          ) : (
            <span />
          )
        }
        interactive={true}
      >
        {children}
      </Tooltip>
      <ConfirmationModal
        open={isConfirmationModalOpen}
        onCancel={() => setModalInfo(undefined)}
        isSubmitDisabled={!!lockedRow}
        {...modalInfo}
      />
    </>
  ) : (
    children
  )
}
